import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { blogPosts } from "@/mocks/blog";
import PageSEO from "@/components/base/PageSEO";

export default function BlogDetail() {
  const { id } = useParams<{ id: string }>();
  const post = blogPosts.find(p => p.id === Number(id));

  // ... existing code ...

  const currentIndex = blogPosts.findIndex(p => p.id === Number(id));
  const prevPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;

  const related = blogPosts.filter(p => p.id !== post!.id && p.category === post!.category).slice(0, 3);
  const otherPosts = related.length < 3
    ? [...related, ...blogPosts.filter(p => p.id !== post!.id && p.category !== post!.category).slice(0, 3 - related.length)]
    : related;

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post!.title,
    "description": post!.excerpt,
    "image": post!.image,
    "datePublished": post!.date,
    "author": { "@type": "Organization", "name": "台中設計師女裝Outlet" },
    "publisher": { "@type": "Organization", "name": "台中設計師女裝Outlet" },
    "mainEntityOfPage": { "@type": "WebPage", "@id": `https://www.example.com/blog/${post!.id}` }
  };

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <PageSEO
        title={`${post!.title}｜台中設計師女裝Outlet 穿搭專欄`}
        description={post!.excerpt}
        keywords={`${post!.tags.join(",")},台中熟齡穿搭,設計師女裝`}
        canonical={`https://www.example.com/blog/${post!.id}`}
        ogImage={post!.image}
        schema={blogSchema}
      />
      <Navbar />
      <main>
        {/* Article Hero */}
        <section className="relative h-[55vh] min-h-[420px] overflow-hidden">
          <img
            src={post!.image}
            alt={post!.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/20 to-black/70" />
          <div className="absolute inset-0 flex flex-col justify-end pb-12 px-6 md:px-16 max-w-[1100px] mx-auto left-0 right-0">
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="px-3 py-1 bg-[#8B5E52] text-white rounded-full text-[0.875rem] font-medium">{post!.category}</span>
              {post!.tags.slice(0, 2).map(tag => (
                <span key={tag} className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white rounded-full text-[0.875rem] font-medium">{tag}</span>
              ))}
            </div>
            <h1 className="text-white text-[2rem] md:text-[2.75rem] font-bold leading-[1.3] mb-4">{post!.title}</h1>
            <div className="flex items-center gap-4 text-white/75 text-[1rem]">
              <span className="flex items-center gap-1.5"><i className="ri-calendar-line"></i>{post!.date}</span>
              <span>·</span>
              <span className="flex items-center gap-1.5"><i className="ri-time-line"></i>閱讀時間 {post!.readTime}</span>
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="bg-white border-b border-[#ede8e0]">
          <div className="w-full px-6 md:px-16 max-w-[1100px] mx-auto py-3">
            <nav className="flex items-center gap-2 text-[0.9375rem] text-[#a09080]">
              <Link to="/" className="hover:text-[#3d2b1f] transition-colors cursor-pointer">首頁</Link>
              <span>/</span>
              <Link to="/blog" className="hover:text-[#3d2b1f] transition-colors cursor-pointer">穿搭專欄</Link>
              <span>/</span>
              <span className="text-[#3d2b1f] line-clamp-1">{post!.title}</span>
            </nav>
          </div>
        </div>

        {/* Article Body */}
        <article className="py-14 md:py-20">
          <div className="w-full px-6 md:px-16 max-w-[1100px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-12">
              {/* Main Content */}
              <div className="flex-1 min-w-0">
                {/* Lead */}
                <p className="text-[#5a4035] text-[1.25rem] leading-[1.8] mb-10 font-medium border-l-4 border-[#c9a98a] pl-6">
                  {post!.excerpt}
                </p>

                {/* Sections */}
                {post!.content.map((section, i) => (
                  <div key={i} className="mb-12">
                    <h2 className="text-[#3d2b1f] text-[1.625rem] font-bold leading-[1.4] mb-5">
                      <a id={`section-${i}`} className="text-[#3d2b1f]">{section.heading}</a>
                    </h2>
                    <p className="text-[#7a6455] text-[1.1875rem] leading-[1.85] mb-6">{section.body}</p>
                    {section.image && (
                      <div className="rounded-lg overflow-hidden w-full h-[300px] md:h-[380px] mb-4">
                        <img
                          src={section.image}
                          alt={section.heading}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                    )}
                  </div>
                ))}

                {/* Tags */}
                <div className="flex flex-wrap gap-2 pt-8 mt-8 border-t border-[#ede8e0]">
                  <span className="text-[#a09080] text-[1rem] mr-1">文章標籤：</span>
                  {post!.tags.map(tag => (
                    <span key={tag} className="px-4 py-1.5 bg-[#f5ede4] text-[#8B5E52] rounded-full text-[0.9375rem] font-medium">{tag}</span>
                  ))}
                </div>

                {/* Prev / Next Navigation */}
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {prevPost ? (
                    <Link
                      to={`/blog/${prevPost.id}`}
                      className="group flex items-center gap-4 p-5 bg-white rounded-lg border border-[#ede8e0] hover:border-[#c9a98a] transition-all cursor-pointer"
                    >
                      <div className="w-8 h-8 flex items-center justify-center text-[#8B5E52] flex-shrink-0 group-hover:-translate-x-1 transition-transform">
                        <i className="ri-arrow-left-line text-xl"></i>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[#a09080] text-[0.8125rem] mb-1">上一篇</p>
                        <p className="text-[#3d2b1f] text-[0.9375rem] font-semibold leading-snug line-clamp-2 group-hover:text-[#8B5E52] transition-colors">{prevPost.title}</p>
                      </div>
                    </Link>
                  ) : <div />}
                  {nextPost ? (
                    <Link
                      to={`/blog/${nextPost.id}`}
                      className="group flex items-center gap-4 p-5 bg-white rounded-lg border border-[#ede8e0] hover:border-[#c9a98a] transition-all cursor-pointer sm:flex-row-reverse text-right"
                    >
                      <div className="w-8 h-8 flex items-center justify-center text-[#8B5E52] flex-shrink-0 group-hover:translate-x-1 transition-transform">
                        <i className="ri-arrow-right-line text-xl"></i>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[#a09080] text-[0.8125rem] mb-1">下一篇</p>
                        <p className="text-[#3d2b1f] text-[0.9375rem] font-semibold leading-snug line-clamp-2 group-hover:text-[#8B5E52] transition-colors">{nextPost.title}</p>
                      </div>
                    </Link>
                  ) : <div />}
                </div>

                {/* CTA Banner */}
                <div className="mt-10 p-8 bg-[#3d2b1f] rounded-lg text-center">
                  <p className="text-[#c9a98a] text-[1rem] tracking-[0.2em] uppercase font-medium mb-3">Discover More</p>
                  <h3 className="text-[#f5e8d8] text-[1.5rem] font-bold mb-3">想親自試穿這些風格單品？</h3>
                  <p className="text-[#c9a98a] text-[1.0625rem] leading-[1.75] mb-6 max-w-md mx-auto">
                    文章中提到的所有款式，都可以在台中門市親自試穿。預約造型顧問，讓您的穿搭更上一層樓。
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <Link to="/shop" className="px-7 py-3 bg-[#c9a98a] text-[#3d2b1f] rounded-full text-[1.0625rem] font-bold hover:bg-[#b8957a] transition-colors cursor-pointer whitespace-nowrap">
                      瀏覽相關商品
                    </Link>
                    <Link to="/appointment" className="px-7 py-3 border-2 border-[#c9a98a] text-[#c9a98a] rounded-full text-[1.0625rem] font-semibold hover:bg-[#c9a98a] hover:text-[#3d2b1f] transition-colors cursor-pointer whitespace-nowrap">
                      預約試穿
                    </Link>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <aside className="w-full lg:w-[300px] flex-shrink-0">
                {/* About Box */}
                <div className="p-6 bg-white rounded-lg mb-6">
                  <h4 className="text-[#3d2b1f] text-[1.125rem] font-bold mb-3">關於本專欄</h4>
                  <p className="text-[#7a6455] text-[1rem] leading-[1.75]">
                    由台中在地造型顧問撰寫，分享適合成熟女性的穿搭技巧與質感生活風格。每篇文章都是實際穿搭經驗的累積，希望讓每位女性都能輕鬆找到屬於自己的優雅風格。
                  </p>
                </div>

                {/* LINE CTA */}
                <div className="p-6 bg-[#f5ede4] rounded-lg mb-6">
                  <h4 className="text-[#3d2b1f] text-[1.125rem] font-bold mb-3">有穿搭問題？</h4>
                  <p className="text-[#7a6455] text-[1rem] leading-[1.75] mb-4">
                    加入 LINE 官方帳號，隨時向我們的造型顧問提問，獲得即時的穿搭建議！
                  </p>
                  <a
                    href="https://line.me"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-[#3d2b1f] text-[#faf8f5] rounded-full text-[1rem] font-semibold hover:bg-[#5a3f30] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-line-fill"></i> 加入 LINE 諮詢
                  </a>
                </div>

                {/* Related Posts */}
                <div>
                  <h4 className="text-[#3d2b1f] text-[1.125rem] font-bold mb-4">相關文章</h4>
                  <div className="space-y-4">
                    {otherPosts.map(p => (
                      <Link
                        key={p.id}
                        to={`/blog/${p.id}`}
                        className="flex gap-3 group cursor-pointer"
                      >
                        <div className="w-20 h-20 rounded-md overflow-hidden flex-shrink-0">
                          <img src={p.image} alt={p.title} className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[#8B5E52] text-[0.8125rem] font-medium mb-1">{p.category}</p>
                          <h5 className="text-[#3d2b1f] text-[0.9375rem] font-semibold leading-[1.45] line-clamp-2 group-hover:text-[#8B5E52] transition-colors">{p.title}</h5>
                          <p className="text-[#a09080] text-[0.8125rem] mt-1">{p.readTime}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
